import { Injectable } from '@angular/core';
import { setActiveAccount } from '@epicuro-next/state/accounts-state';
import {
  selectOpenedList,
  selectOpenedListProducts,
} from '@epicuro-next/state/product-list-state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { ScannerApiService } from '../service/scanner-api.service';

import {
  addToBasket,
  addToBasketFail,
  addToBasketSuccess,
  addToList,
  addToListFail,
  addToListSuccess,
  barcodeDuped,
  barcodeRead,
  clearBarcodeResults,
  clearCurrentBarcodeForNext,
  findProduct,
  noTargetSelected,
  productFound,
  productNotFound,
} from './barcode-reader.actions';
import {
  selectBarcodeProcessResults,
  selectLoadingProduct,
  selectScanTarget,
} from './barcode-reader.selectors';

export const FIND_PRODUCT_DELAY = 500;
const ERROR_MESSAGE_READ_DURATION = 2500;
const SUCCESS_MESSAGE_READ_DURATION = 500;

@Injectable()
export class ScannerEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private scannerApi: ScannerApiService,
  ) {}

  private clearCurrentBarcodeTimer: ReturnType<typeof setTimeout>;

  public clearBarcodeResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setActiveAccount),
      map(() => clearBarcodeResults()),
    ),
  );

  public startBarcodeSearch$ = createEffect(() =>
    this.actions$.pipe(
      ofType(barcodeRead),
      delay(FIND_PRODUCT_DELAY),
      withLatestFrom(this.store.pipe(select(selectLoadingProduct))),
      filter(([_, loading]) => !loading),
      map(([{ barcode }]) => findProduct({ barcode })),
    ),
  );

  public findProduct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(findProduct),
      withLatestFrom(this.store.pipe(select(selectBarcodeProcessResults))),
      switchMap(([{ barcode }, results]) => {
        const prevResult = results.find((r) => r.barcode === barcode);
        if (prevResult) {
          if (prevResult.product) {
            return of(productFound({ barcode, product: prevResult.product }));
          } else {
            return of(productNotFound({ barcode }));
          }
        }

        return this.scannerApi.searchProductByBarcode(barcode).pipe(
          map((result) => {
            const product = result.data.productByBarcode;
            if (product) return productFound({ product, barcode });
            return productNotFound({ barcode });
          }),
          catchError(() => of(productNotFound({ barcode }))),
        );
      }),
    ),
  );

  public addToTarget$ = createEffect(() =>
    this.actions$.pipe(
      ofType(productFound),
      withLatestFrom(
        this.store.pipe(select(selectScanTarget)),
        this.store.pipe(select(selectOpenedListProducts)),
        this.store.pipe(select(selectOpenedList)),
      ),
      map(([{ product, barcode }, target, openedListProducts, openedList]) => {
        if (!target) return noTargetSelected();

        if (target.type === 'List') {
          const isAlreadyInList =
            target.list.id === openedList?.id
              ? openedListProducts.find(
                  (listProduct) => listProduct.productId === product.productId,
                )
              : false;
          if (isAlreadyInList) {
            return barcodeDuped({ barcode, target });
          }
          return addToList({ barcode, product, target });
        } else {
          return addToBasket({ barcode, product });
        }
      }),
    ),
  );

  public clearStatusAfterInactivitySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(addToBasketSuccess, addToListSuccess, barcodeDuped),
        tap(() => {
          clearTimeout(this.clearCurrentBarcodeTimer);
          this.clearCurrentBarcodeTimer = setTimeout(() => {
            this.store.dispatch(clearCurrentBarcodeForNext());
          }, SUCCESS_MESSAGE_READ_DURATION);
        }),
      ),
    {
      dispatch: false,
    },
  );

  public clearStatusAfterInactivityFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          addToBasketFail,
          addToListFail,
          productNotFound,
          noTargetSelected,
        ),
        tap(() => {
          clearTimeout(this.clearCurrentBarcodeTimer);
          this.clearCurrentBarcodeTimer = setTimeout(() => {
            this.store.dispatch(clearCurrentBarcodeForNext());
          }, ERROR_MESSAGE_READ_DURATION);
        }),
      ),
    {
      dispatch: false,
    },
  );
}
